import { supabaseClient } from 'supabase'

/**
 * desk-paste's refusal codes (33-03 `deskPaste.ts` ErrorCode, a contract: add,
 * never rename), plus two the client adds itself: `network` (no response at
 * all) and `unknown` (a response that is not the contract).
 */
export type DeskPasteErrorCode =
  | 'method_not_allowed'
  | 'unauthenticated'
  | 'not_admin'
  | 'bad_request'
  | 'misconfigured'
  | 'cap_reached'
  | 'login_wall'
  | 'link_refused'
  | 'link_failed'
  | 'payload_too_large'
  | 'not_retryable'
  | 'intake_failed'
  | 'empty'
  | 'text_too_long'
  | 'too_many_images'
  | 'too_big'
  | 'wrong_type'
  | 'network'
  | 'unknown'

export type DeskPasteEditor = 'started' | 'already' | 'unavailable'

/** desk-paste's 202 body (33-06). */
export type DeskPasteAccepted = {
  raw_item_id: number
  action: string
  editor: DeskPasteEditor
  run_id?: string
}

/**
 * What a paste or a retry came to. A refusal keeps its code (and `cap` for
 * cap_reached) so the panel can say exactly what happened; `code` stays a
 * string so a code added server-side later still reaches the generic error.
 */
export type DeskPasteOutcome =
  | { ok: true; value: DeskPasteAccepted }
  | { ok: false; code: DeskPasteErrorCode | string; cap: number | null; status: number | null }

export type PasteToDeskParams = {
  text: string
  images: File[]
}

const EDITOR_STATES: ReadonlyArray<string> = ['started', 'already', 'unavailable']

const isAccepted = (data: unknown): data is DeskPasteAccepted => {
  if (!data || typeof data !== 'object') return false
  const body = data as Record<string, unknown>
  return (
    typeof body.raw_item_id === 'number' &&
    Number.isSafeInteger(body.raw_item_id) &&
    typeof body.editor === 'string' &&
    EDITOR_STATES.includes(body.editor)
  )
}

/**
 * supabase-js answers a non-2xx with a FunctionsHttpError (or a relay error)
 * whose `context` is the Response. A fetch failure has no Response there.
 */
const readRefusal = async (error: unknown): Promise<DeskPasteOutcome> => {
  const context = (error as { context?: unknown } | null)?.context as
    | { status?: unknown; json?: () => Promise<unknown> }
    | undefined
  if (!context || typeof context.json !== 'function') {
    return { ok: false, code: 'network', cap: null, status: null }
  }
  const status = typeof context.status === 'number' ? context.status : null
  let body: unknown = null
  try {
    body = await context.json()
  } catch {
    body = null
  }
  const fields = body && typeof body === 'object' ? (body as Record<string, unknown>) : {}
  const code = typeof fields.code === 'string' && fields.code.length > 0 ? fields.code : 'unknown'
  const cap = typeof fields.cap === 'number' ? fields.cap : null
  return { ok: false, code, cap, status }
}

/**
 * The one door to desk-paste. `functions.invoke` sends the admin's OWN
 * session token (supabase-js adds `Authorization: Bearer <access_token>` and
 * the anon `apikey`); no shared secret exists in the admin (D-17, T-33-60).
 *
 * A FormData body goes out as multipart: functions-js sets no Content-Type
 * for FormData, so the browser writes the boundary (33-RESEARCH A1, checked
 * against functions-js 2.80). No fetch fallback is needed.
 */
const invokeDeskPaste = async (body: FormData | { retry_raw_item_id: number }): Promise<DeskPasteOutcome> => {
  try {
    const { data, error } = await supabaseClient.functions.invoke('desk-paste', { body })
    if (error) return readRefusal(error)
    if (!isAccepted(data)) return { ok: false, code: 'unknown', cap: null, status: null }
    return { ok: true, value: data }
  } catch {
    return { ok: false, code: 'network', cap: null, status: null }
  }
}

/**
 * Paste text, a single https link, and/or up to five images (D-16). The text
 * part is left out when it is blank, so an image-only paste reads as one.
 * Image 1 is the first appended; desk-paste keeps that order.
 */
export const pasteToDesk = ({ text, images }: PasteToDeskParams): Promise<DeskPasteOutcome> => {
  const form = new FormData()
  if (text.trim().length > 0) form.append('text', text)
  for (const image of images) form.append('image', image, image.name)
  return invokeDeskPaste(form)
}

/**
 * Ask the editor again to read a saved paste that is still `new` (D-20). It
 * stores nothing new, so desk-paste skips the daily cap for it.
 */
export const retryPaste = (rawItemId: number): Promise<DeskPasteOutcome> =>
  invokeDeskPaste({ retry_raw_item_id: rawItemId })
